import { ApiError, type ApiErrorBody } from "@/lib/api-client";

/**
 * Upload a single {@link File} as multipart/form-data with byte-accurate
 * progress callbacks. Uses {@link XMLHttpRequest} because {@code fetch}
 * does not expose upload progress events in any browser today.
 *
 * The Authorization header is set by the caller (typically read from
 * {@link useAuthStore}); the refresh-on-401 interceptor used by
 * {@code apiRequest} does NOT apply here — if the upload returns 401 the
 * caller should fail open and let the user reload. Adding refresh
 * integration for XHR is a future task (see batch brief).
 *
 * On non-2xx responses we surface the same {@link ApiError} shape as
 * {@code apiRequest} so the upstream {@code localizeError} helper works
 * uniformly across both transport paths.
 */
export interface UploadProgressEvent {
  loaded: number;
  total: number;
  percent: number;
}

export interface UploadOptions {
  /** The form field name. Backend expects {@code files}. */
  field?: string | undefined;
  /** Bearer token to send. {@code null} skips Authorization. */
  authToken: string | null;
  /** Called on each upload-progress tick. Always invoked with 100 on success. */
  onProgress?: ((event: UploadProgressEvent) => void) | undefined;
  /** AbortSignal — when fired, aborts the in-flight request. */
  signal?: AbortSignal | undefined;
}

export function uploadFileWithProgress<T = unknown>(
  url: string,
  file: File,
  options: UploadOptions,
): Promise<T> {
  const field = options.field ?? "files";

  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url);

    // The withCredentials flag mirrors apiRequest's credentials: "include"
    // so the refresh-token cookie still rides along.
    xhr.withCredentials = true;

    if (options.authToken) {
      xhr.setRequestHeader("Authorization", `Bearer ${options.authToken}`);
    }
    // Note: do NOT set Content-Type — the browser inserts the multipart
    // boundary automatically. Setting it manually breaks the upload.

    if (options.onProgress) {
      xhr.upload.onprogress = (e: ProgressEvent<EventTarget>) => {
        if (e.lengthComputable) {
          const percent = Math.min(100, Math.round((e.loaded / e.total) * 100));
          options.onProgress?.({ loaded: e.loaded, total: e.total, percent });
        }
      };
    }

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        // Make sure the progress bar finishes — some browsers skip the
        // 100% tick when the response arrives sooner than the last frame.
        options.onProgress?.({
          loaded: file.size,
          total: file.size,
          percent: 100,
        });
        try {
          if (xhr.responseText.length === 0) {
            resolve(undefined as T);
            return;
          }
          resolve(JSON.parse(xhr.responseText) as T);
        } catch {
          resolve(undefined as T);
        }
      } else {
        let body: ApiErrorBody | null = null;
        try {
          body = JSON.parse(xhr.responseText) as ApiErrorBody;
        } catch {
          /* ignore — body wasn't json */
        }
        reject(
          new ApiError(
            xhr.status,
            body,
            body?.message ?? `Upload failed with status ${xhr.status}`,
          ),
        );
      }
    };

    xhr.onerror = () => {
      reject(new ApiError(0, null, "Lỗi mạng khi tải ảnh"));
    };
    xhr.onabort = () => {
      reject(new ApiError(0, null, "Đã hủy tải ảnh"));
    };

    if (options.signal) {
      if (options.signal.aborted) {
        xhr.abort();
        return;
      }
      options.signal.addEventListener("abort", () => xhr.abort(), { once: true });
    }

    const form = new FormData();
    form.append(field, file, file.name);
    xhr.send(form);
  });
}
